<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import VariableSection from './VariableSection.vue';
import FileUpload from 'dashboard/components-next/file-upload/FileUpload.vue';
import { MEDIA_FORMATS, UPLOAD_CONFIG } from 'dashboard/helper/templateHelper';
import {
  parseLocationFromUrl,
  isValidLocation,
  formatLocationDisplay,
} from 'dashboard/helper/locationHelper';
import LocationExtractorAPI from 'dashboard/api/whatsapp/locationExtractor';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  parameterType: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const locationUrl = ref('');
const parsedLocation = ref(null);
const isExtractingLocation = ref(false);



const headerData = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const getUploadConfig = computed(() => {
  // Defensive check for UPLOAD_CONFIG
  if (!UPLOAD_CONFIG || !headerData.value?.format) return {};
  return UPLOAD_CONFIG[headerData.value.format] || {};
});

// Watch for initial location data
watch(
  () => props.modelValue,
  newValue => {
    if (
      newValue?.format === 'LOCATION' &&
      newValue.media?.location &&
      !parsedLocation.value
    ) {
      parsedLocation.value = newValue.media.location;
      if (!locationUrl.value) {
        locationUrl.value = `https://www.google.com/maps?q=${newValue.media.location.latitude},${newValue.media.location.longitude}`;
      }
    }
  },
  { immediate: true, deep: true }
);

const headerFormats = computed(() => [
  { value: 'TEXT', label: t('SETTINGS.TEMPLATES.BUILDER.HEADER.FORMAT_TEXT'), icon: 'i-lucide-type' },
  { value: 'IMAGE', label: t('SETTINGS.TEMPLATES.BUILDER.HEADER.FORMAT_IMAGE'), icon: 'i-lucide-image' },
  { value: 'VIDEO', label: t('SETTINGS.TEMPLATES.BUILDER.HEADER.FORMAT_VIDEO'), icon: 'i-lucide-video' },
  { value: 'DOCUMENT', label: t('SETTINGS.TEMPLATES.BUILDER.HEADER.FORMAT_DOCUMENT'), icon: 'i-lucide-file-text' },
  { value: 'LOCATION', label: t('SETTINGS.TEMPLATES.BUILDER.HEADER.FORMAT_LOCATION'), icon: 'i-lucide-map-pin' },
]);

const exampleKey = computed(() => {
  return props.parameterType === 'positional'
    ? 'header_text'
    : 'header_text_named_params';
});

const headerTextData = computed({
  get: () => ({
    text: headerData.value.text,
    examples: headerData.value.example?.[exampleKey.value] || [],
    error: headerData.value.error,
  }),
  set: ({ text, examples, error }) => {
    headerData.value = {
      ...headerData.value,
      text,
      example: {
        ...headerData.value.example,
        [exampleKey.value]: [...examples],
      },
      error,
    };
  },
});

const handleLocationUrl = async value => {
  locationUrl.value = value;

  if (!value) {
    parsedLocation.value = null;
    headerData.value = {
      ...headerData.value,
      error: '',
    };
    return;
  }

  // First try client-side parsing
  const location = parseLocationFromUrl(value);
  if (location && isValidLocation(location)) {
    parsedLocation.value = location;

    headerData.value = {
      ...headerData.value,
      media: {
        location: location,
      },
      error: '',
    };
    return;
  }

  // If client-side parsing fails and it looks like a URL, try server-side extraction
  if (value.startsWith('http://') || value.startsWith('https://')) {
    try {
      isExtractingLocation.value = true;
      if (!LocationExtractorAPI) {
        throw new Error('LocationExtractorAPI is not initialized');
      }
      const response = await LocationExtractorAPI.extract(value);

      if (response.data && !response.data.error) {
        const extractedLocation = {
          latitude: response.data.latitude,
          longitude: response.data.longitude,
          name: response.data.name,
          address: response.data.address,
        };

        parsedLocation.value = extractedLocation;
        headerData.value = {
          ...headerData.value,
          media: {
            location: extractedLocation,
          },
          error: '',
        };
      } else {
        parsedLocation.value = null;
        headerData.value = {
          ...headerData.value,
          error: response.data?.error || t('SETTINGS.TEMPLATES.BUILDER.HEADER.EXTRACT_LOCATION_ERROR'),
        };
      }
    } catch (error) {
      console.error('Location Extraction Failed:', error);
      parsedLocation.value = null;
      headerData.value = {
        ...headerData.value,
        error: t('SETTINGS.TEMPLATES.BUILDER.HEADER.EXTRACT_LOCATION_ERROR_HELP'),
      };
    } finally {
      isExtractingLocation.value = false;
    }
  } else {
    parsedLocation.value = null;
    headerData.value = {
      ...headerData.value,
        error: t('SETTINGS.TEMPLATES.BUILDER.HEADER.LOCATION_HELP'),
    };
  }
};

const setHeaderFormat = format => {
  if (MEDIA_FORMATS.includes(format)) {
    const newValue = {
      ...headerData.value,
      format,
      text: '',
      example: {
        ...headerData.value.example,
        header_text: [],
        header_text_named_params: [],
      },
      media: {},
      error: '',
    };
    headerData.value = newValue;
  } else {
    headerData.value = {
      ...headerData.value,
      format,
      media: {},
      error: '',
    };
  }
};

const handleUploadStart = () => {
  headerData.value = {
    ...headerData.value,
    media: {
      ...headerData.value.media,
      uploading: true,
    },
    error: '',
  };
};

const handleUploadError = errorMessage => {
  headerData.value = {
    ...headerData.value,
    media: {
      ...headerData.value.media,
      uploading: false,
    },
    error: errorMessage,
  };
};

const handleMediaUpload = mediaData => {
  if (mediaData) {
    headerData.value = {
      ...headerData.value,
      media: {
        blobId: mediaData.blobId,
        blobKey: mediaData.blobKey,
        fileUrl: mediaData.fileUrl,
        fileName: mediaData.fileName,
        contentType: mediaData.contentType,
        uploading: false,
      },
      error: '',
    };
  } else {
    headerData.value = {
      ...headerData.value,
      media: {},
      error: '',
    };
  }
};
</script>

<template>
  <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-3">
        <span class="i-lucide-layout-panel-top size-5 text-n-slate-11" />
        <h4 class="font-medium text-n-slate-12">
          {{ t('SETTINGS.TEMPLATES.BUILDER.HEADER.TITLE') }}
        </h4>
        <span class="text-xs text-n-slate-11 bg-n-solid-3 px-2 py-1 rounded">
          {{ t('SETTINGS.TEMPLATES.BUILDER.OPTIONAL') }}
        </span>
      </div>
      <div class="flex items-center">
        <Switch v-model="headerData.enabled" />
      </div>
    </div>

    <div v-if="headerData.enabled" class="space-y-4">
      <!-- Header Type Selection -->
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ t('SETTINGS.TEMPLATES.BUILDER.HEADER.TYPE') }}
        </label>
        <div class="flex gap-2">
          <NextButton
            v-for="format in headerFormats"
            :key="format.value"
            :label="format.label"
            :icon="format.icon"
            size="sm"
            :variant="headerData.format === format.value ? 'solid' : 'outline'"
            :color="headerData.format === format.value ? 'blue' : 'slate'"
            @click="setHeaderFormat(format.value)"
          />
        </div>
      </div>
      <div v-if="headerData.format === 'TEXT'">
        <VariableSection
          v-model="headerTextData"
          input-type="input"
          :max-length="60"
          :placeholder="t('SETTINGS.TEMPLATES.BUILDER.HEADER.TEXT_PLACEHOLDER')"
          :label="t('SETTINGS.TEMPLATES.BUILDER.HEADER.TEXT_LABEL')"
          :help-text="t('SETTINGS.TEMPLATES.BUILDER.HEADER.TEXT_HELP')"
          :parameter-type="parameterType"
          :max-variables="1"
        />
      </div>
      <!-- Location Input -->
      <div v-if="headerData.format === 'LOCATION'" class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            📍
            {{
              t('SETTINGS.TEMPLATES.BUILDER.HEADER.LOCATION_URL')
            }}
          </label>
          <p class="text-xs text-n-slate-11 mb-3">
            {{
              t('SETTINGS.TEMPLATES.BUILDER.HEADER.LOCATION_HELP')
            }}
          </p>
          <Input
            :model-value="locationUrl"
            type="text"
            :placeholder="t('SETTINGS.TEMPLATES.BUILDER.HEADER.LOCATION_PLACEHOLDER')"
            :disabled="isExtractingLocation"
            @update:model-value="handleLocationUrl"
          />

          <!-- Loading State -->
          <div
            v-if="isExtractingLocation"
            class="flex items-center gap-2 mt-3 p-3 bg-n-blue-2 border border-n-blue-6 rounded-lg"
          >
            <span
              class="i-lucide-loader-2 size-4 text-n-blue-9 animate-spin flex-shrink-0"
            />
            <p class="text-xs text-n-blue-10">
              {{ t('SETTINGS.TEMPLATES.BUILDER.HEADER.EXTRACT_LOCATION_LOADING') }}
            </p>
          </div>

          <!-- Success State with Map Preview -->
          <div
            v-if="parsedLocation && isValidLocation(parsedLocation)"
            class="mt-3 space-y-2"
          >
            <div
              class="flex items-start gap-2 p-3 bg-n-green-2 border border-n-green-6 rounded-lg"
            >
              <span
                class="i-lucide-check-circle size-4 text-n-green-9 flex-shrink-0 mt-0.5"
              />
              <div class="flex-1 min-w-0">
                <p class="text-xs font-medium text-n-green-11 mb-1">
                  {{ t('SETTINGS.TEMPLATES.BUILDER.HEADER.EXTRACT_LOCATION_SUCCESS') }}
                </p>
                <p class="text-xs text-n-green-10">
                  {{ formatLocationDisplay(parsedLocation) }}
                </p>
              </div>
            </div>

            <!-- Map Preview -->
            <div
              class="relative w-full h-48 rounded-lg overflow-hidden border border-n-weak"
            >
              <iframe
                :src="`https://www.google.com/maps?q=${parsedLocation.latitude},${parsedLocation.longitude}&output=embed`"
                width="100%"
                height="100%"
                style="border: 0"
                loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <!-- Error State -->
          <div
            v-if="
              !isExtractingLocation &&
              locationUrl &&
              (!parsedLocation || !isValidLocation(parsedLocation)) &&
              headerData.error
            "
            class="flex items-start gap-2 mt-3 p-3 bg-n-ruby-2 border border-n-ruby-6 rounded-lg"
          >
            <span
              class="i-lucide-alert-circle size-4 text-n-ruby-9 flex-shrink-0 mt-0.5"
            />
            <div class="flex-1 min-w-0">
              <p class="text-xs font-medium text-n-ruby-11 mb-1">
                {{ t('SETTINGS.TEMPLATES.BUILDER.HEADER.EXTRACT_LOCATION_ERROR') }}
              </p>
              <p class="text-xs text-n-ruby-10 mb-2">{{ headerData.error }}</p>
              <p class="text-xs text-n-slate-11 italic">
                {{ t('SETTINGS.TEMPLATES.BUILDER.HEADER.LOCATION_TIP') }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Other Media Types (Image, Video, Document) -->
      <div
        v-else-if="
          MEDIA_FORMATS.includes(headerData.format) &&
          headerData.format !== 'LOCATION'
        "
        class="space-y-4"
      >
        <FileUpload
          :model-value="headerData.media"
          :accept="getUploadConfig.accept"
          :format="headerData.format"
          :label="t('SETTINGS.TEMPLATES.BUILDER.HEADER.MEDIA_UPLOAD')"
          :uploading="!!headerData.media?.uploading"
          :error="headerData.error"
          @update:model-value="handleMediaUpload"
          @upload-start="handleUploadStart"
          @upload-error="handleUploadError"
        />
        <div
          v-if="headerData.error"
          class="bg-red-50 border border-red-200 rounded-lg p-3"
        >
          <div class="flex items-center gap-2 text-sm text-red-600">
            <span class="i-lucide-alert-circle size-4" />
            <span>{{ headerData.error }}</span>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-center py-8 text-n-slate-11">
      <span class="i-lucide-layout-panel-top size-12 mx-auto mb-3 opacity-50" />
      <p class="text-sm">
        {{ t('SETTINGS.TEMPLATES.BUILDER.HEADER.DISABLED_TEXT') }}
      </p>
    </div>
  </div>
</template>
